// owner-map: OP-02.spa
// Forecast-resume command subsystem (EPIC #157, phase P5.6 — design §2.2).
//
// `reattachToForecast` (reconnect to an in-flight ROOT forecast's SSE stream), the
// unified Ring-1 hydrate+resume sweep (`resumeForecasts` — resumes every running root +
// branch after the initial server load hydrates the store), and `reattachBranch`
// (reconnect to an in-flight branch's SSE stream) moved VERBATIM out of
// `hooks/useForecastResume.js`. The only behavioral rewrites are the migration-mandated
// seams:
//   • `runStateRef.current`    → `deps.store.getState()`   (retires §7 #1; the live store
//                                 read is now first-class + unit-testable)
//   • `execByRunIdRef.current` → the `execTable` module singleton (§7 #3)
//   • `dispatchRun(…)`         → `deps.store.dispatch(…)`
//   • the two `await import("../lib/forecastStream.js")` for `resumeForecastStream` →
//     `deps.services.forecastClient.resumeForecastStream` (the I/O seam Lane A fakes)
//   • React handles (setters, App callbacks, imperative refs) threaded through the deps
//     bags below instead of closed over.
// The `reattachBranchRef` forward-ref DISSOLVES: the sweep and `reattachBranch` now live
// in one module, so the sweep calls `reattachBranch(deps, handle)` directly. Everything
// else — the §7 #2 abort/cancel gates, the exec claim/release-in-`finally`, the compare-leg
// focus logic, the deliberately-incomplete reasoning — is byte-preserved. Do NOT "tidy" it.
//
// The `/api/forecasts/:trace/result` fetches go through `deps.services.api.getResult` (P5.11
// drained the raw fetches); the load-bearing 404-recovery branches on the returned `status`.
//
// SCOPE NOTE (P6/P7): the `ui`/`callbacks`/`refs` bags are the honest shape of a verbatim
// command extraction — resume is entangled with React UI state. P6 already pruned two:
// `loadedRef`/`ring1ResumeFiredRef` are GONE — `bootForecasts` awaits `loadForecasts` before
// ever calling `resumeForecasts`, once, from a single mount effect, so the fire-once guard and
// the load-completion poll are dead by construction (no other caller exists). `forecastsRef`
// is also gone — `reattachBranch` resolves the root via a live, uncapped store read
// (`selectFocusableForecastById`, either slice) instead of a React-state mirror of the capped display list
// (which was also a latent correctness gap: the capped list can miss an old root). What's left
// (`autoZoomRef`) is a view-layer imperative handle, not resume-domain state — P7's job to
// reclassify, not this phase's. Don't re-grow this list.
import { recordUiEvent } from "../lib/logRingBuffer";
import { persistMessage } from "../lib/messages";
import { userFacingRunError } from "../lib/forecast/runErrorText.js";
import {
  activeSet,
  runStarted,
  runCreated,
  traceAllocated,
  runProgress,
  runCancelled,
  runErrored,
  runCompleted,
  entryAppended,
  comparisonSet,
} from "../lib/forecast/reducer.js";
import { makeRun, makeEntry, STATUS, filterAndStampEvents } from "../lib/forecast/model.js";
import { editBufferOf, extractDistribution } from "../lib/forecast/multiway.js";
import { resolutionRightBufferFor } from "../lib/timeline/multiwayMarker.js";
import {
  assessmentText,
  hasForecastSummaryMessage,
  summaryConvId,
} from "../lib/forecast/summaryPost.js";
import { entryToMessageBody } from "../lib/forecast/hydrate.js";
import {
  clearPendingForecastIfOwned,
  contractFromPreprocessed,
  isAbortLikeResumeError,
} from "../lib/forecast/forecastClient.js";
import { progressPayload } from "../lib/forecast/progress.js";
import { withBackground } from "../lib/forecast/contractBackground.js";
import { applyNarrationFrame } from "../lib/forecast/narrationRouter.js";
import { applyUpdateFrame } from "../lib/forecast/updateRouter.js";
import {
  selectFocusableForecastById,
  selectPremiseRunsById,
  selectRun,
  selectRunByTraceId,
} from "../lib/forecast/selectors.js";
import { fmtPct } from "../lib/forecast/formatPct.js";
import {
  planResume,
  pickBranchReattachCreatedAt,
  shouldClearGlobalPhaseOnTerminal,
  resolveSingleRootFocus,
  treeRootOf,
} from "../lib/forecast/resume.js";
import {
  PENDING_FORECAST_KEY,
  clearSharedRerunHandle,
  readSharedRerunHandles,
} from "../lib/forecast/storageKeys.js";
import { loadSharedForecasts } from "./loadSharedForecasts.js";
import { canUpdateNow } from "../lib/forecast/sharedRowGuards.js";
import { pruneComparePending, readComparePending } from "../lib/comparePending";
import { readViewFromLocation } from "../lib/view/urlSync.js";
import { execTable } from "../store/execTable.js";
import { resumedRunObs } from "../lib/forecast/resumeDuration.js";
import { bindIntent } from "../lib/telemetry/intentDeps.js";
import { noteForecastOpen } from "../lib/telemetry/forecastOpen.js";
import { beginIntent, type IntentHandle } from "../lib/telemetry/instrument.js";
import type { CommandDeps } from "./deps.js";
import {
  boundedResultInspection,
  isRecoveryWaitExpiredError,
  isUnansweredRecoveryError,
  resolveRecoveryDeadlineAt,
  resumeForecastStreamThroughEof,
} from "../lib/forecastStream.js";

// ── deps typing ───────────────────────────────────────────────────────────────
// Pragmatic types at the React boundary (decision C): store/services carry real domain
// types; the UI setters / App callbacks / imperative refs are typed loosely (React handles
// this command doesn't own). Tighten as P6/P7 move them.
type AnyFn = (...args: any[]) => any;
type Ref<T = any> = { current: T };

function boundedProbability(value: unknown): number | null {
  const trimmed = typeof value === "string" ? value.trim() : null;
  const parsed = typeof value === "number" ? value : trimmed ? Number(trimmed) : Number.NaN;
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : null;
}

/** React setters the resume ops write (ephemeral UI state; moves down in P7). */
interface ResumeUi {
  setForecastingPhase: AnyFn;
  setPendingPreprocess: AnyFn;
  setLastObs: AnyFn;
  forecastLiveProgressEnabled?: boolean;
  setCompareMode?: AnyFn;
  debugUI?: boolean;
}

/** App-provided callbacks the resume ops orchestrate. */
interface ResumeCallbacks {
  loadConversationThread: AnyFn;
}

/**
 * Stable imperative handles.
 *   • autoZoomRef — App-owned; read via `.current` so the []-stable reattach frames against
 *                   the live measured width. A view-layer handle, not resume-domain state —
 *                   P7's "imperative handles down into components" carve-out, not P6's.
 */
interface ResumeRefs {
  autoZoomRef: Ref;
}

/** Full deps for the resume subsystem: the shared base + the React-bound bags. */
export interface ResumeDeps extends CommandDeps {
  ui: ResumeUi;
  callbacks: ResumeCallbacks;
  refs: ResumeRefs;
}

/**
 * Reconnect to an in-flight ROOT forecast's SSE stream. Used by App's `selectForecast`
 * (log-click on a running row) and by `resumeForecasts` (the cross-reload sweep).
 * `cancelledRef` lets the caller abort if the component unmounts mid-reattach.
 */
export async function reattachToForecast(
  deps: ResumeDeps,
  pending: any,
  cancelledRef: any,
  opts: any = {},
) {
  // One `forecast.resume` per reattached run (P2 design §4.6), opened once the reattach
  // claims the run; it ends when the reattach settles. The run's own intent (bound to its
  // trace) still ends with the run.
  const box: ResumeIntentBox = { intent: null };
  try {
    const out = await reattachToForecastUnder(deps, pending, cancelledRef, opts, box);
    box.intent?.end(resumeOutcome(deps, box.runId, cancelledRef?.current === true));
    return out;
  } catch (e) {
    box.intent?.end("error");
    throw e;
  }
}

/** The `forecast.resume` a reattach opened, once it claimed the run, and that run's id. */
interface ResumeIntentBox {
  intent: IntentHandle<"forecast.resume"> | null;
  runId?: string | null;
}

/**
 * How a settled reattach went: the reattach handles a failed run itself (it dispatches
 * `runErrored` and returns), so the run's state, not the return, says whether it failed.
 */
function resumeOutcome(
  deps: ResumeDeps,
  runId: string | null | undefined,
  cancelled: boolean,
): "ok" | "error" | "cancelled" {
  if (cancelled) return "cancelled";
  const status = runId ? selectRun(deps.store.getState(), runId)?.status : null;
  if (status === STATUS.ERRORED) return "error";
  if (status === STATUS.CANCELLED) return "cancelled";
  return "ok";
}

async function reattachToForecastUnder(
  rawDeps: ResumeDeps,
  pending: any,
  cancelledRef: any,
  opts: any,
  box: ResumeIntentBox,
) {
  let deps = rawDeps;
  let terminalOutcome = false;
  const isCancelled = () => cancelledRef && cancelledRef.current === true;
  // seedChat=false (a tour-time peek at a running row) drives the stream and
  // forecast state silently, without writing the "Resuming forecast…"
  // placeholder or its stage labels into the chat — the tour narration stays.
  const seedChat = opts.seedChat !== false;
  // opts.lane==="secondary": this is the NON-focused leg of a compare-pair reload
  // resume. The primary leg owns the global single-active UI — `active`, the
  // `forecastingPhase` awaiting animation, and the chat thread — so a secondary
  // reattach must NOT touch any of those (two reattaches writing `active`/
  // `forecastingPhase` would fight and flip the focused lane). It only drives its
  // stream to terminal and writes its row into `forecasts[]` (keyed by placeholderId),
  // so the lane resolves + renders; its awaiting animation comes from the data-driven
  // per-lane predicate in the render, not the global forecastingPhase. Default
  // (undefined / "primary") keeps the original single-active behavior verbatim.
  const isSecondary = opts.lane === "secondary";
  // opts.compare: this reattach is one leg of a compare PAIR. Drives the engine-name
  // prefix on the reconnected chat placeholder (so the user can tell which backend each
  // reconnected leg is) — matching the live fresh-run placeholders, which are prefixed.
  // A compare-pair leg (writes the "Resuming…" chat line + engine prefix into the
  // shared merged thread). Keyed on opts.compare ONLY — both compare legs pass
  // compare:true (see comparePending). Crucially NOT `|| isSecondary`: the
  // passive background-resume sweep passes {lane:"secondary"} WITHOUT compare, and
  // must write NO chat (it drives a background root's stream/row silently). Coupling
  // isCompareLeg to isSecondary would leak a dangling "Resuming…" placeholder into
  // the active conversation for every non-focused running root on a multi-root reload.
  const isCompareLeg = opts.compare === true;
  // A retained leg from an older compare generation resumes as an ordinary
  // background root, but still owns a comparePending handle that must be
  // removed when that stream reaches a true terminal state.
  const clearsComparePendingOnTerminal =
    isCompareLeg || opts.clearComparePendingOnTerminal === true;
  // Single-active globals, no-op'd for the secondary leg (the primary owns them).
  // Every terminal/result branch routes its setForecastingPhase / activeSet dispatch
  // through these so the secondary never clears the focused lane's phase or active.
  // EXCEPTION (bug e2a493ce): a secondary leg that is completing the run the user
  // is currently focused on MUST clear the global phase. On reload a single running
  // root resumes passively (secondary); if the user then re-selects that still-running
  // row, handleSelect sets forecastingPhase="awaiting" but the foreground reattach
  // early-returns on the exec claim (this secondary sweep already owns the stream), so
  // nobody else is left to clear it — without this the "Generating Forecast" sweep
  // sticks forever after the resume completes. The selection is asked live, at terminal
  // time, so it reflects the user's latest choice — and asked only "is the run this reattach
  // carries (`reattachRunId`) the one on screen?", an equality gate on an id already in hand,
  // never for a target (C-10). See shouldClearGlobalPhaseOnTerminal for the exact rule + its
  // unit tests; `resumeForecasts.interleaving.test.js` pins both answers across a switch.
  const setPhaseGlobal = (v: any) => {
    const reattachRunOnScreen =
      reattachRunId != null && deps.store.getState().activeRunId === reattachRunId;
    if (shouldClearGlobalPhaseOnTerminal({ isSecondary, reattachRunOnScreen }))
      deps.ui.setForecastingPhase(v);
  };
  // dual-write all reattach terminal active clears to store
  const setActiveGlobal = (v: any) => {
    if (!isSecondary) {
      deps.store.dispatch(activeSet(v?.id ?? null));
    }
  };
  // Resolve runId for exec table. pending.clientId IS the runId (client-minted UUID).
  // reattachRunId: prefer the run's own client id; for legacy entries without one,
  // resolve via the live store by traceId (deps.store.getState() reads live store state, so this
  // works once the server-load HYDRATED dispatch has populated the store). null if
  // neither resolves → claim(null) returns false below and we skip (no run to reattach).
  const reattachRunId =
    pending.clientId || selectRunByTraceId(deps.store.getState(), pending.traceId)?.runId || null;
  // Idempotent attach: if this tab already streams this trace, do nothing.
  // exec.claim(null,...) returns false, preserving the early-return guard for the rare
  // case where reattachRunId cannot be resolved (legacy no-clientId + empty store).
  if (
    pending?.traceId &&
    !execTable.claim(reattachRunId, {
      kind: "main",
      clientId: pending.clientId || null,
    })
  )
    return;
  box.runId = reattachRunId;
  box.intent = beginIntent(
    "forecast.resume",
    { forecast_id: reattachRunId, trace_id: pending?.traceId ?? null },
    { origin: opts.origin ?? "auto" },
  );
  deps = bindIntent(rawDeps, box.intent);
  const q = pending.question || "";
  const placeholderResolutionDate = new Date(Date.now() + 365 * 86400000)
    .toISOString()
    .slice(0, 10);
  const bareContract = {
    title: q,
    question: q,
    resolution_date: placeholderResolutionDate,
    resolution_criteria: "",
    background: "",
    sources: [],
    probability: null,
  };
  // Show the preprocessed contract (resolution date / criteria) during the
  // resumed run instead of bare question text until `done`. Sources, in
  // priority order, all merged over the bare fallback:
  //   1) pending.preprocessed — a page reload carries the preprocessed
  //      payload on the resume handle (no React/forecasts[] state yet).
  //   2) pending.contract — a log-click resume passes the forecasts[] row's
  //      contract (the submit-time earlyContract in-tab, or the server-
  //      persisted preprocessed contract after a reload-then-click).
  // pending.contract wins last so a richer in-memory/server contract beats a
  // skeleton; a degenerate {probability:null}-only contract just leaves the
  // preprocessed/bare fields intact.
  const ppContract = contractFromPreprocessed(pending.preprocessed, q, placeholderResolutionDate);
  const placeholderContract = {
    ...bareContract,
    ...(ppContract || {}),
    ...(pending.contract || {}),
  };
  // Prefer the wire-side client_id (matches the optimistic placeholder
  // and the proxy lifecycle row, so save-effect upsert merges instead of
  // creating a duplicate). forecastId is the internal UUID and would
  // double-write. fc-resume-* is the fallback for legacy entries with
  // neither (very rare).
  const placeholderId = pending.clientId || pending.forecastId || "fc-resume-" + Date.now();
  const fid = pending.forecastId || null;
  // Carry traceId on the placeholder so the row this reattach writes into
  // forecasts[] stays reattach-able: selectForecast/log-click gate reattach on
  // forecast.traceId, so a placeholder without it would make a later click on
  // this still-running row a no-op ("timeline/chat don't update").
  // Stamp the orchestrator into serverContext.metadata so rootEngine(root) resolves
  // this RUNNING placeholder's engine label (purple/green lane cue) instead of
  // "Unknown" during the awaiting phase. _engine is in-session only (lost on reload),
  // and a DB running row's server_ctx is '{}' until completion — the resume handle's
  // engine (carried on `pending`) is the one durable source we have here. Only set it
  // when known so we never write a bogus {orchestrator:null}.
  // forecasts[] write ALWAYS happens (even for secondary) — the running placeholder
  // row (_pending/status:"running") is what compareRoots resolves to and what the
  // per-lane awaiting predicate animates. Only the single-active globals are gated.
  // Dual-write placeholder creation (or resume) to the store.
  // If the run already exists (e.g. from HYDRATED — a log-click resume on a
  // still-running row), preserve its persisted fields (contract, events, history,
  // schedule, etc.) by using runStarted rather than runCreated. runCreated would
  // replace the hydrated run with a skeleton, losing all those fields.
  if (reattachRunId) {
    if (selectRun(deps.store.getState(), reattachRunId)) {
      deps.store.dispatch(runStarted(reattachRunId));
    } else {
      deps.store.dispatch(
        runCreated(
          makeRun({
            runId: reattachRunId,
            conversationId: reattachRunId,
            question: q,
            status: STATUS.RUNNING,
            ...(pending.engine ? { backend: pending.engine } : {}),
          }),
        ),
      );
    }
    deps.store.dispatch(
      traceAllocated(reattachRunId, {
        traceId: pending.traceId || null,
        forecastId: fid,
        backend: pending.engine || null,
      }),
    );
  }
  // Load persisted message history for the primary leg only — secondary legs
  // (background non-focused roots in the resume sweep) must not clobber the
  // focused conversation's messages, and compare legs get their thread from the
  // compare-hydration effect. This covers ALL reattach entries: self-submitted
  // PENDING_FORECAST_KEY reload, running-root resume sweep, and log-click
  // reattach that routes through selectForecast → here. The stale-guard
  // (loadMessagesLatestRef) drops the response if a later navigation has
  // claimed the slot, so a mid-flight reload or click doesn't write stale rows.
  if (fid && reattachRunId && !isSecondary) {
    const reattachConvId =
      selectRun(deps.store.getState(), reattachRunId)?.conversationId ?? reattachRunId;
    deps.callbacks.loadConversationThread(fid, reattachConvId);
  }
  if (!isSecondary) {
    noteForecastOpen("auto", { keep: true });
    deps.store.dispatch(activeSet(reattachRunId)); // dual-write reattach resume active to store
    deps.ui.setForecastingPhase("awaiting");
    // Fit the timeline to the resumed forecast's resolution span — mirrors the
    // fresh-submit path (autoZoom at submit). Without this, reloading while a
    // forecast is still generating leaves the timeline at the reload's default
    // scale ("will not rescale to fit while generating even after reload"):
    // the resume path never reframed. placeholderContract carries the best-known
    // resolution date (preprocessed/persisted contract merged over the 1-year
    // fallback). autoZoom reads the live width via cSizeRef, so invoking it from
    // this long-lived ([]-deps) callback frames against the real measured width.
    if (placeholderContract?.resolution_date) {
      // Explicit right-hand buffer, same reason as the boot-restore path in
      // bootForecasts.ts: autoZoom's own buffer describes whatever is in `active`, which on
      // a cold reload is nothing, so it falls back to the narrow %-label reservation and
      // opens zoomed in past CENTER.
      const buckets =
        extractDistribution(placeholderContract, {
          allMultiway: true,
          probability: (placeholderContract as any)?.probability,
        })?.buckets ?? null;
      deps.refs.autoZoomRef.current?.(
        placeholderContract.resolution_date,
        true,
        null,
        resolutionRightBufferFor(buckets, { assumeMarker: true }),
      );
    }
    // Reconnect → RESET the thread (not append) so a log-click on a running row
    // shows a clean question turn instead of stacking onto the previously-viewed
    // forecast's chat. On a page reload the thread is already empty, so reset and
    // append coincide. The "Resuming forecast…" status line is now run-projected
    // (selectChatProjection derives the running event line from run.status), so we
    // seed only the real question turn — no placeholder.
    // Secondary leg skips this — the primary already seeded the chat and the
    // compare-hydration effect restores the merged thread.
  }
  // SECONDARY compare leg (seedChat:false): no chat seed — its running line is
  // run-projected too; the compare-hydration effect restores the merged thread.

  // Re-surface the preprocess confirmation on a page-reload resume so the
  // forecast's question/contract isn't silently skipped past. Gated on:
  //   - seedChat — a tour-time silent peek never pops UI.
  //   - pending.preprocessed — only the reload handle carries it; a log-click
  //     resume (forecasts[] row) has no preprocessed payload and relies on the
  //     still-live React pendingPreprocess state instead.
  //   - !pending.preprocessConfirmed — the user already dismissed/edited the
  //     panel before reloading (dismiss() stamps the handle), so don't nag.
  // clientId=placeholderId matches the panel's `active?.id===clientId` gate
  // (the store now drives active, so placeholderId is the key).
  if (seedChat && pending.preprocessed && !pending.preprocessConfirmed) {
    deps.ui.setPendingPreprocess({
      original: pending.original || q,
      question: pending.preprocessed,
      engine: pending.engine || "",
      editing: false,
      editText: editBufferOf(pending.preprocessed.question_text || q, pending.routing),
      clientId: placeholderId,
      // Absent on a handle written before routing was kept: an edit then takes the binary default.
      ...(pending.routing ? { routing: pending.routing } : {}),
    });
  }

  // The pending "Resuming forecast…" line and its per-tick stage label are now
  // run-projected: selectChatProjection emits the running event line from
  // run.status, so the resume SSE no longer rewrites a chat placeholder on progress.
  // (Dispatching run.progress on the resume path — so the projected line shows the
  // live stage text — is deferred to a later change that migrates progress to the store.)
  // All terminal chat writes go through here so a tour-time peek (seedChat=false)
  // completes silently: it drops the pending placeholder and appends the terminal
  // message only when we're seeding chat. Forecast/active state still updates
  // regardless (handled at each call site) — only the chat is gated.
  const writeTerminalChat = (msg: any, { skipPersist = false, skipStoreDispatch = false } = {}) => {
    if (!seedChat) return;
    // Dispatch to the store so selectChatProjection can render it.
    // skipPersist=true for run-projected lines (prediction results).
    // skipStoreDispatch=true for prediction results that will be emitted by the
    // completed run's selectChatProjection terminal line (no double entry).
    if (!skipStoreDispatch) {
      const wtEntry = makeEntry({
        role: msg.role || "assistant",
        content: msg.content,
        runId: reattachRunId,
      });
      const wtRun = selectRun(deps.store.getState(), reattachRunId) ?? null;
      const wtConvId = wtRun?.conversationId ?? reattachRunId;
      if (wtConvId) deps.store.dispatch(entryAppended(wtConvId, wtEntry));
      if (!skipPersist) persistMessage(fid, entryToMessageBody(wtEntry));
    }
  };

  // Drive resume stream — first event is `catchup` (current row state),
  // then progress until terminal. We mostly care about the terminal:
  // done → fetch result_url + render; error/cancelled → surface to user.
  // The `gotTerminal` flag distinguishes "stream ended cleanly with a
  // terminal event" from "stream ended with nothing" — the latter would
  // strand the placeholder forever without an explicit fallback branch.
  try {
    const { resumeForecastStream } = deps.services.forecastClient;
    let resultUrl = null,
      recoveredResult = null,
      errPayload = null,
      cancelledTerm = false,
      gotTerminal = false,
      resumeAnnounced = false;
    // The first `catchup` that saw the row still running, stamped with its browser-clock
    // arrival: its database-computed `elapsed_ms` dates the completion line's duration
    // (see resumedRunObs). Never subtract `started_at` from the browser clock.
    let runningCatchup: any = null;
    // Announce the resume to the ring buffer + lastObs once per attach.
    // Without this, a bug filed from a reloaded tab would have
    // forecaster_trace_id=null on the bug_reports row (lastObs is only
    // populated by the fresh-doForecast path), and triagers would have
    // no visible signal that the user resumed after a reload.
    // Accept either shape: catchup uses {last_stage,last_progress}, progress
    // uses {stage,progress}. Normalize so the recorded event carries context
    // regardless of which type fires first.
    const announceResume = (d: any) => {
      if (resumeAnnounced) return;
      resumeAnnounced = true;
      const last_stage = d?.last_stage ?? d?.stage ?? null;
      const last_progress = d?.last_progress ?? d?.progress ?? null;
      recordUiEvent({
        event: "ui.forecast.resume",
        traceId: pending.traceId,
        last_stage,
        last_progress,
      });
      deps.ui.setLastObs({
        traceId: pending.traceId,
        backend: pending.engine || null,
        requestId: null,
        latencyMs: null,
        ts: new Date().toISOString(),
      });
    };
    // Log-click reattach has only row identity; use the reload sweep's durable
    // deadline without borrowing another run's single-slot handle.
    const persistedHandles = readComparePending();
    try {
      const single = JSON.parse(sessionStorage.getItem(PENDING_FORECAST_KEY) || "null");
      if (single) persistedHandles.push(single);
    } catch (_e) {
      /* best-effort storage */
    }
    const persisted = persistedHandles.filter(
      (handle: any) => handle.traceId === pending.traceId && handle.clientId === pending.clientId,
    );
    const originalStartedAt =
      pending.recoveryStartedAt ??
      pending.submittedAt ??
      persisted[0]?.recoveryStartedAt ??
      persisted[0]?.submittedAt ??
      // This run's start, restamped on a reforecast; createdAt is the forecast's, and would
      // expire an older forecast's window at once (a log-click reattach carries neither).
      selectRun(deps.store.getState(), reattachRunId)?.startedAt ??
      selectRun(deps.store.getState(), reattachRunId)?.createdAt;
    const recoveryDeadlineAt = resolveRecoveryDeadlineAt(
      originalStartedAt,
      Math.min(
        ...[
          opts.recoveryDeadlineAt,
          pending.recoveryDeadlineAt,
          ...persisted.map((handle: any) => handle.recoveryDeadlineAt),
        ].filter(Number.isFinite),
      ),
      opts.now || Date.now,
    );
    // Typed-progress cursor (#5611): a reload has none (the server replays the run's
    // typed log from scratch); a tab that already folded this run's typed frames asks
    // for strictly after its progress document, so nothing is folded twice.
    const foldedSeq = selectRun(deps.store.getState(), reattachRunId)?.progressDoc?.seq;
    const sinceSeq =
      typeof foldedSeq === "number" && Number.isSafeInteger(foldedSeq) && foldedSeq >= 0
        ? foldedSeq
        : null;
    for await (const evt of resumeForecastStreamThroughEof(pending.traceId, undefined, {
      resumeStream: resumeForecastStream,
      getResult: (traceId: string, options: { signal?: AbortSignal }) =>
        deps.services.api.getResult(traceId, options),
      sinceSeq,
      recoveryDeadlineAt,
      now: opts.now,
      sleep: opts.recoverySleep,
      retryDelayMs: opts.resumeRetryDelayMs,
    })) {
      if (isCancelled()) return;
      if (evt.type === "update" && deps.ui.forecastLiveProgressEnabled && reattachRunId) {
        applyUpdateFrame(deps.store.dispatch, deps.store.getState, reattachRunId, evt.data);
      } else if (evt.type === "narration" && deps.ui.forecastLiveProgressEnabled && reattachRunId) {
        // Same reasoning as the branch reattach below: prior beats are gone,
        // but a still-running forecast must keep narrating after a reload.
        applyNarrationFrame(deps.store.dispatch, deps.store.getState, reattachRunId, evt.data);
      } else if (evt.type === "done") {
        announceResume(null);
        if (typeof evt.data?.result_url === "string" && evt.data.result_url.length > 0) {
          resultUrl = evt.data.result_url;
        } else {
          errPayload = {
            code: "invalid_resume_done",
            message: "Resume stream emitted invalid done event",
          };
        }
        gotTerminal = true;
        if (errPayload) break;
      } else if (evt.type === "error") {
        announceResume(null);
        errPayload = evt.data || {};
        gotTerminal = true;
        break;
      } else if (evt.type === "cancelled") {
        announceResume(null);
        cancelledTerm = true;
        gotTerminal = true;
        break;
      } else if (evt.type === "recoveredResult") {
        announceResume(null);
        recoveredResult = evt.data;
        gotTerminal = true;
      } else if (evt.type === "invalidDone") {
        announceResume(null);
        errPayload = {
          code: "invalid_resume_done",
          message: "Resume stream emitted invalid done event",
        };
        gotTerminal = true;
        break;
      } else if (evt.type === "catchup") {
        // catchup carries the row's last_stage/last_progress at reattach time.
        // Dispatch to the store so the run-projected running line shows the
        // last-known stage text immediately on resume.
        announceResume(evt.data || {});
        const d = evt.data || {};
        if (!runningCatchup && d.status === "running")
          runningCatchup = { ...d, receivedAt: (opts.now || Date.now)() };
        const stage = d.last_stage ?? d.stage ?? null;
        const progress = d.last_progress ?? d.progress ?? null;
        const step = d.last_step ?? d.step ?? null;
        const eng = selectRun(deps.store.getState(), reattachRunId)?.backend ?? null;
        // Dispatch on EITHER stage or step — progressPayload resolves a step index
        // from `step` alone, and the tap can persist last_stage null with last_step set.
        if (reattachRunId && (stage != null || step != null)) {
          deps.store.dispatch(
            runProgress(reattachRunId, progressPayload(stage, progress, step, d.data ?? null, eng)),
          );
        }
      } else if (evt.type === "progress") {
        announceResume(evt.data);
        const d = evt.data || {};
        const eng = selectRun(deps.store.getState(), reattachRunId)?.backend ?? null;
        if (reattachRunId && (d.stage != null || d.step != null)) {
          deps.store.dispatch(
            runProgress(
              reattachRunId,
              progressPayload(d.stage, d.progress, d.step ?? null, d.data ?? null, eng),
            ),
          );
        }
      }
    }
    if (isCancelled()) return;
    // True-terminal cleanup for a COMPARE leg: drop this leg's comparePending handle so
    // a finished leg doesn't get re-resumed on a later reload (a still-running leg's
    // handle persists — see the mount effect). Only on a genuine terminal (done / error /
    // cancelled / "no terminal emitted") — NOT in the catch below, where a thrown
    // network drop should keep the handle so a reload can retry the maybe-still-running
    // forecast. Mirrors the submit path's per-leg removal in genForecastFromServer.
    const removeCompareLegOnTerminal = () => {
      if (!clearsComparePendingOnTerminal || !pending.traceId) return;
      import("../lib/comparePending.js")
        .then(({ removeComparePendingLeg }) => removeComparePendingLeg(pending.traceId))
        .catch(() => {}); // swallow-ok: cleanup — best-effort handle drop after a terminal
    };
    if (cancelledTerm) {
      terminalOutcome = true;
      // skipPersist+skipStoreDispatch: the cancelled terminal line is run-projected from
      // run.status (selectChatProjection emits "Forecast cancelled" for CANCELLED runs).
      // No persisted entry — projection is the single owner to avoid double-render on reload.
      writeTerminalChat(
        { role: "assistant", content: "Forecast was cancelled." },
        { skipPersist: true, skipStoreDispatch: true },
      );
      setPhaseGlobal(null);
      setActiveGlobal(null);
      if (reattachRunId) deps.store.dispatch(runCancelled(reattachRunId)); // dual-write cancel
      removeCompareLegOnTerminal();
    } else if (errPayload) {
      terminalOutcome = true;
      // skipPersist+skipStoreDispatch: the errored terminal line is run-projected from
      // run.error (selectChatProjection emits run.error text for ERRORED runs).
      // No persisted entry — projection is the single owner to avoid double-render on reload.
      writeTerminalChat(
        {
          role: "assistant",
          content: userFacingRunError(errPayload.message, errPayload.code),
        },
        { skipPersist: true, skipStoreDispatch: true },
      );
      setPhaseGlobal(null);
      setActiveGlobal(null);
      if (reattachRunId) deps.store.dispatch(runErrored(reattachRunId, errPayload)); // dual-write error
      removeCompareLegOnTerminal();
    } else if (resultUrl || recoveredResult) {
      // Fetch the persisted result via the typed api seam (P5.11). getResult reports a 2xx
      // with a null body (bad JSON) as ok:false, so `rr.result` is a real ForecastResult
      // whenever `rr.ok` — no null-deref here.
      const rr = recoveredResult
        ? { ok: true, status: 200, result: recoveredResult }
        : await boundedResultInspection(
            pending.traceId,
            undefined,
            (traceId: string, options: { signal?: AbortSignal }) =>
              deps.services.api.getResult(traceId, options),
            {
              recoveryDeadlineAt,
              now: opts.now,
              resultInspectionTimeoutMs: opts.resultInspectionTimeoutMs,
            },
          );
      if (isCancelled()) return;
      // 404 is "not durable yet"; status 0 is the api client's folded network failure — no
      // answer at all. Neither ends the run.
      if (!rr.ok && (rr.status === 404 || rr.status === 0)) {
        writeTerminalChat(
          {
            role: "assistant",
            content: "Forecast result is not available yet. Reopen this forecast to check again.",
          },
          { skipPersist: true },
        );
        setPhaseGlobal(null);
        return false;
      }
      terminalOutcome = true;
      if (rr.ok) {
        const result: any = rr.result; // decision C: verbatim code reads result loosely
        const eventsList = Array.isArray(result.events) ? filterAndStampEvents(result.events) : [];
        // Defensive resolution_criteria backfill — genForecastFromServer does this
        // for the live path, but a forecast completing via resume/reattach skips it.
        // When the server contract omits resolution_criteria, the Contract panel's
        // RESOLUTION section renders blank. Backfill from the preprocessed question
        // or the run's already-stored (pre-reload) contract, which carried the
        // preprocess criteria.
        if (
          result.contract &&
          (!result.contract.resolution_criteria ||
            !String(result.contract.resolution_criteria).trim())
        ) {
          const fallbackCriteria =
            pending.preprocessed?.resolution_criteria || pending.contract?.resolution_criteria;
          // Require non-whitespace content — consistent with the missing-check's
          // trim() above, so a whitespace-only fallback doesn't keep the drawer blank.
          if (fallbackCriteria && String(fallbackCriteria).trim()) {
            result.contract.resolution_criteria = fallbackCriteria;
            console.warn(
              "[resume] result contract missing resolution_criteria; backfilled from preprocessed/stored contract",
            );
          }
        }
        const nfc = {
          id: placeholderId,
          forecastId: fid,
          traceId: pending.traceId || null,
          question: q,
          createdAt: new Date().toISOString(),
          branches: [],
          serverPrediction:
            typeof result.contract?.probability === "number"
              ? result.contract.probability
              : parseFloat(result.contract?.probability) || 0,
          serverExplanation: result.explanation || "",
          // The wire's `contract.context` is the record's `background`.
          contract: withBackground(result.contract) || placeholderContract,
          events: eventsList,
          report: result.report || null,
          serverContext: {
            explanation: result.explanation || "",
            metadata: {
              ...(result.metadata || {}),
              ...(pending.engine ? { orchestrator: pending.engine } : {}),
            },
            question: result.question || {},
            report: result.report || null,
          },
          status: "completed",
        };
        // This branch does NOT re-assert active. The run completes by its own
        // runId — runCompleted(reattachRunId) — so a late result lands on the
        // correct run regardless of which forecast is now active (the store is
        // keyed by runId, never clobbering the focused forecast). The terminal
        // chat line goes through writeTerminalChat, which is gated only on
        // seedChat (set at reattach start: false for the passive background sweep,
        // so those write no chat).
        const prob = fmtPct(nfc.contract?.probability || 0);
        // NOT persisted and NOT dispatched as a dialogue entry —
        // the result is run-projected from the COMPLETED run via selectChatProjection
        // (runCompleted below is the record).
        writeTerminalChat(
          { role: "assistant", content: `Forecast finished: ${prob}%` },
          { skipPersist: true, skipStoreDispatch: true },
        );
        if (reattachRunId)
          deps.store.dispatch(
            runCompleted(reattachRunId, {
              contract: nfc.contract || null,
              serverPrediction: nfc.serverPrediction,
              events: nfc.events || [],
              serverContext: nfc.serverContext || null,
              forecastId: fid ?? undefined,
              serverExplanation: nfc.serverExplanation || "",
              report: nfc.report || null,
              obs: resumedRunObs(
                selectRun(deps.store.getState(), reattachRunId)?.obs,
                runningCatchup,
                (opts.now || Date.now)(),
              ),
            }),
          ); // dual-write completion
        // Post the report summary + "Full Report" link into this run's OWN conversation.
        // The fresh-submit path does this via pushSayerMsg; the resume/reattach path
        // finalized the run WITHOUT it, so a forecast that completed while the tab was
        // closed (or while the user watched another forecast) never showed its summary on
        // click-back — the deep-forecast gap. Dispatch directly, mirroring writeTerminalChat's
        // convId resolution + persistence, and gate on hasForecastSummaryMessage so a run that
        // ALSO finalized via fresh submit can't double-post: exactly once from any path.
        const resumeSummary = assessmentText(nfc);
        if (
          reattachRunId &&
          resumeSummary &&
          !hasForecastSummaryMessage(deps.store.getState(), reattachRunId)
        ) {
          const convId = summaryConvId(deps.store.getState(), reattachRunId);
          const summaryEntry = makeEntry({
            role: "assistant",
            content: resumeSummary,
            runId: reattachRunId,
            metadata: { reportLink: true },
          });
          deps.store.dispatch(entryAppended(convId, summaryEntry));
          persistMessage(fid, entryToMessageBody(summaryEntry));
        }
      } else {
        if (reattachRunId)
          deps.store.dispatch(
            runErrored(reattachRunId, {
              message: `Result fetch returned ${rr.status}`,
            }),
          );
        writeTerminalChat({
          role: "assistant",
          content:
            "This forecast finished, but Sayer couldn't load the result. Reopen it to try again.",
        });
        setActiveGlobal(null);
      }
      setPhaseGlobal(null);
      removeCompareLegOnTerminal(); // done (result fetched or fetch-failed) — leg is finished
    } else if (!gotTerminal) {
      terminalOutcome = true;
      // Stream closed without emitting any terminal event — rare but
      // possible if upstream proxy buffers stall or the row was reaped
      // mid-resume. Don't leave the placeholder hanging.
      writeTerminalChat({
        role: "assistant",
        content:
          "Sayer lost track of this forecast. It may have finished or been cancelled. Open it again from your forecasts.",
      });
      setPhaseGlobal(null);
      setActiveGlobal(null);
      if (reattachRunId)
        deps.store.dispatch(
          runErrored(reattachRunId, {
            code: "no_terminal",
            message: "Stream closed without terminal event",
          }),
        ); // W4
      removeCompareLegOnTerminal(); // the row is gone/finished server-side — don't retry it
    }
  } catch (e) {
    if (isCancelled()) return;
    if (isRecoveryWaitExpiredError(e)) {
      // The forecast may still be alive beyond this tab's original absolute
      // wait budget. Stop active waiting without declaring failure or deleting
      // the durable handle; a later explicit selection can inspect it again.
      writeTerminalChat(
        {
          role: "assistant",
          content: "Forecast is still running in the background.",
        },
        { skipPersist: true },
      );
      setPhaseGlobal(null);
    } else if (isUnansweredRecoveryError(e)) {
      // A result read that stalled (or died without a status) is not an answer: the run
      // may still be live or already durable. Same non-terminal handling as a 404 after
      // `done` — keep the run and its handle, and let a later selection read again.
      writeTerminalChat(
        {
          role: "assistant",
          content: "Forecast result is not available yet. Reopen this forecast to check again.",
        },
        { skipPersist: true },
      );
      setPhaseGlobal(null);
    } else if (isAbortLikeResumeError(e)) {
      // Reload aborted the resume SSE — the forecast is still running
      // server-side and the next load reconnects. Show a TRANSIENT (not
      // persisted) status and leave the run resumable: do NOT persist a
      // sticky error, flip the run to errored, or blank the active forecast.
      writeTerminalChat(
        { role: "assistant", content: "Reconnecting to forecast…" },
        { skipPersist: true, skipStoreDispatch: true },
      );
      setPhaseGlobal(null);
    } else {
      terminalOutcome = true;
      // Genuine resume failure — surface it (transient line; the errored run
      // is the durable record via runErrored + selectChatProjection).
      writeTerminalChat(
        {
          role: "assistant",
          content: userFacingRunError((e as any)?.message || String(e)),
        },
        { skipPersist: true, skipStoreDispatch: true },
      );
      setPhaseGlobal(null);
      setActiveGlobal(null);
      if (reattachRunId) deps.store.dispatch(runErrored(reattachRunId, e)); // dual-write catch error
    }
  } finally {
    // Release the exec claim so a future log-click or reload-resume
    // can reattach once this stream settles (success, error, cancel, or
    // isCancelled early-return). Runs even when isCancelled() returns early
    // from inside the try/catch — finally always fires before the return.
    if (pending?.traceId && reattachRunId) execTable.release(reattachRunId); // release exec claim
  }
  return terminalOutcome;
}

/**
 * Reconnect to an in-flight BRANCH's SSE stream (the SOLE branch-reattach path). Called
 * by `resumeForecasts`'s `kind:'branch'` sweep entries and by `resumeSharedRerun`. The root is resolved via a live,
 * uncapped store read over both slices (`selectFocusableForecastById`) rather than the capped `forecasts[]`
 * display list a React-state mirror ref would carry — so an old root beyond the display cap
 * still resolves.
 */
export async function reattachBranch(
  deps: ResumeDeps,
  handle: any,
  opts: { now?: () => number } = {},
) {
  const box: ResumeIntentBox = { intent: null, runId: handle?.placeholderId ?? null };
  try {
    const out = await reattachBranchUnder(deps, handle, opts, box);
    box.intent?.end(resumeOutcome(deps, box.runId, false));
    return out;
  } catch (e) {
    box.intent?.end("error");
    throw e;
  }
}

async function reattachBranchUnder(
  rawDeps: ResumeDeps,
  handle: any,
  opts: { now?: () => number },
  box: ResumeIntentBox,
) {
  let deps = rawDeps;
  // Skip if this branch is already being streamed in this tab — either by an
  // in-flight genBranch or a prior reattach still running. The reattach effect
  // re-runs on every focus (me changes), so without this each focus would
  // append a duplicate "Resuming branch / Branch: …" line and open a competing
  // SSE stream. Cleared in the finally below once this attempt settles.
  // Exec table keyed by runId = handle.placeholderId (the branch client-minted UUID).
  if (
    !execTable.claim(handle.placeholderId, {
      kind: "branch",
      clientId: handle.placeholderId,
      rootClientId: handle.rootClientId || null,
      rootForecastId: handle.rootForecastId || null,
      parentBranchId: handle.parentBranchId || null,
      placeholderId: handle.placeholderId,
    })
  )
    return;
  try {
    // Find the root in the store, in either slice (a branch of a forecast shared with the
    // viewer has its root in `sharedRunsById`); bail if it's gone (deleted in another tab).
    // `rootForecastId` is null when the branch was started before the root's server UUID
    // was populated, so the fallback lookup only fires when it's actually set — otherwise
    // the lookup would fall back to a forecastId scan matching every forecast whose UUID
    // hasn't landed yet (false positive). It tries a direct runId hit before falling back to
    // a forecastId scan, so a single call against `rootClientId` covers both id shapes.
    const state = deps.store.getState();
    // LegacyForecast is a pragmatically-typed open record (decision C) — cast at this
    // boundary rather than threading a narrower shape through just for these two reads.
    const root: any =
      selectFocusableForecastById(state, handle.rootClientId) ||
      (handle.rootForecastId ? selectFocusableForecastById(state, handle.rootForecastId) : null);
    if (!root) {
      // Root not hydrated YET — skip this attempt. The sweep can fire before a compare
      // SECONDARY root has landed in the store (effect-ordering / load race); the branch
      // row is still in the server list, so a later sweep re-fire (reattachToForecast
      // identity change) recovers it. The execByRunIdRef claim above is released in the
      // finally on this early exit. No `forecast.resume` yet: the retry is the resume.
      return;
    }
    box.intent = beginIntent(
      "forecast.resume",
      { forecast_id: handle.placeholderId ?? null, trace_id: handle.traceId ?? null },
      { origin: "auto" },
    );
    deps = bindIntent(rawDeps, box.intent);
    // Local placeholder bag for the resumed branch. Only `.id` is load-bearing:
    // every store dispatch below keys off placeholderBranch.id. The resume
    // path reads branch data from the handle (not this bag) and from the fetched
    // result; the completedBranch spread reads no field from this bag other than
    // id (all other props are overridden by the result). Slimmed to just id (M3).
    const placeholderBranch = {
      id: handle.placeholderId,
    };
    // Dual-write reattach branch placeholder to the store — NON-DESTRUCTIVELY.
    // runCreated OVERWRITES runsById[runId] (reducer spreads the new run over the
    // slot), so blindly re-creating here clobbers a run already hydrated from the
    // server (or advanced by a concurrent stream): it would reset createdAt to NOW
    // — making the chat line JUMP to the bottom on reload (the projection orders run
    // lines by createdAt) — and reset status/progress to a blank RUNNING placeholder
    // — leaving it STUCK on "Generating forecast…". Only create when the run is
    // absent, and stamp createdAt from the handle's submittedAt (the branch's real
    // creation time) so the reattached line keeps its place next to its ACK entry.
    if (!selectRun(deps.store.getState(), placeholderBranch.id)) {
      deps.store.dispatch(
        runCreated(
          makeRun({
            runId: placeholderBranch.id,
            conversationId: root.id,
            parentRunId: handle.parentBranchId || root.id,
            forkDate: handle.forkDate || null,
            modification: handle.modification,
            status: STATUS.RUNNING,
            forkEvent: handle.forkEvent || null,
            // Reuse the createdAt snapshotted at placeholder creation (carried verbatim
            // in the handle) — never re-derive it here, or the chat line jumps.
            createdAt: pickBranchReattachCreatedAt(handle),
          }),
        ),
      );
    }
    // Stamp traceId/forecastId/backend on the resumed branch run so sub-branching
    // resolves parent_forecast_id correctly and the engine/lane cue is preserved.
    // Mirrors the live create path (createBranch/streamBranch stamp via onTraceAllocated).
    // traceAllocated is write-once (stampRun) so a duplicate is safe.
    deps.store.dispatch(
      traceAllocated(placeholderBranch.id, {
        traceId: handle.traceId,
        forecastId: handle.branchForecastId ?? null,
        backend: handle.engine ?? null,
      }),
    );
    recordUiEvent({
      event: "branch.reattach_start",
      placeholderId: handle.placeholderId,
      traceId: handle.traceId,
      rootClientId: handle.rootClientId || null,
      rootForecastId: handle.rootForecastId || null,
    });
    // (exec slot already claimed at the guard above — no second claim needed)
    // The branch's running status line and per-tick stage label are run-projected:
    // selectChatProjection emits the branch run's running event line from run.status,
    // so the resume SSE no longer seeds or rewrites a _branchTrace-keyed chat
    // placeholder. runProgress dispatches below keep the projected line animated.

    // Resume SSE.
    const { resumeForecastStream } = deps.services.forecastClient;
    let resultUrl = null;
    let errPayload = null;
    let cancelledTerm = false;
    // The first `catchup` that saw the branch still running: its `elapsed_ms` + arrival time
    // date the duration (resumedRunObs).
    let runningCatchup: any = null;
    // Typed-progress cursor (#5611): a branch this tab already folded asks for strictly
    // after its progress document, so a replay never folds its log twice (activity and
    // unknown entries accumulate; revision counters increment). A reload has no cursor.
    // Branch reattach has no EOF recovery yet (#5625), so this single request is the
    // only place the cursor is needed here.
    const foldedBranchSeq = selectRun(deps.store.getState(), placeholderBranch.id)?.progressDoc
      ?.seq;
    const branchSinceSeq =
      typeof foldedBranchSeq === "number" &&
      Number.isSafeInteger(foldedBranchSeq) &&
      foldedBranchSeq >= 0
        ? foldedBranchSeq
        : null;
    try {
      for await (const evt of resumeForecastStream(handle.traceId, undefined, {
        sinceSeq: branchSinceSeq,
      })) {
        if (evt.type === "update" && deps.ui.forecastLiveProgressEnabled) {
          applyUpdateFrame(
            deps.store.dispatch,
            deps.store.getState,
            placeholderBranch.id,
            evt.data,
          );
        } else if (evt.type === "narration" && deps.ui.forecastLiveProgressEnabled) {
          // The run is still live after a reattach, so it is still producing
          // beats. Prior beats are gone (narration is ephemeral by design and
          // the stream has no seq to resume from), but NEW ones must keep
          // arriving or a reload silently stops narrating a running forecast.
          applyNarrationFrame(
            deps.store.dispatch,
            deps.store.getState,
            placeholderBranch.id,
            evt.data,
          );
        } else if (evt.type === "done") {
          resultUrl = evt.data?.result_url;
        } else if (evt.type === "error") {
          errPayload = evt.data || {};
          break;
        } else if (evt.type === "cancelled") {
          cancelledTerm = true;
          break;
        } else if (evt.type === "catchup") {
          const d = evt.data || {};
          if (!runningCatchup && d.status === "running")
            runningCatchup = { ...d, receivedAt: (opts.now || Date.now)() };
          const stage = d.last_stage ?? d.stage ?? null;
          const progress = d.last_progress ?? d.progress ?? null;
          const step = d.last_step ?? d.step ?? null;
          const eng = selectRun(deps.store.getState(), placeholderBranch.id)?.backend ?? null;
          if (stage != null || step != null) {
            deps.store.dispatch(
              runProgress(
                placeholderBranch.id,
                progressPayload(stage, progress, step, d.data ?? null, eng),
              ),
            );
          }
        } else if (evt.type === "progress") {
          const d = evt.data || {};
          const eng = selectRun(deps.store.getState(), placeholderBranch.id)?.backend ?? null;
          if (d.stage != null || d.step != null) {
            deps.store.dispatch(
              runProgress(
                placeholderBranch.id,
                progressPayload(d.stage, d.progress, d.step ?? null, d.data ?? null, eng),
              ),
            );
          }
        }
      }
    } catch (e) {
      if (isAbortLikeResumeError(e)) {
        // Reload aborted the branch resume SSE — leave the branch running and
        // resumable (the next load's kind:'branch' sweep reconnects) and show a
        // transient "Reconnecting…" status on the run-projected branch line
        // instead of flipping it to errored.
        const cur: any = selectRun(deps.store.getState(), placeholderBranch.id)?.progress || {};
        deps.store.dispatch(
          runProgress(placeholderBranch.id, {
            stage: cur.stage ?? "reconnecting",
            pct: cur.pct ?? cur.fraction ?? 0,
            message: "Reconnecting to scenario…",
          }),
        );
        execTable.release(placeholderBranch.id);
        recordUiEvent({
          event: "branch.reattach_terminal",
          placeholderId: handle.placeholderId,
          traceId: handle.traceId,
          outcome: "reconnecting",
        });
        return;
      }
      errPayload = { code: "reattach_failed", message: String(e) };
    }

    if (cancelledTerm) {
      // Dual-write reattach cancelled to the store. Run RETAINED.
      execTable.release(placeholderBranch.id);
      deps.store.dispatch(runCancelled(placeholderBranch.id));
      recordUiEvent({
        event: "branch.reattach_terminal",
        placeholderId: handle.placeholderId,
        traceId: handle.traceId,
        outcome: "cancelled",
      });
      return;
    }
    if (errPayload) {
      // Dual-write reattach error to the store. Run RETAINED.
      execTable.release(placeholderBranch.id);
      deps.store.dispatch(runErrored(placeholderBranch.id, errPayload));
      recordUiEvent({
        event: "branch.reattach_terminal",
        placeholderId: handle.placeholderId,
        traceId: handle.traceId,
        outcome: "error",
        code: errPayload?.code || null,
      });
      return;
    }
    // From here on every non-success exit must commit a terminal run state to the
    // store, or the placeholder + its run-projected chat line stay stranded forever.
    // (The stuck "Resuming branch: …" bug: row completed, but a server restart wiped
    // the in-memory result → result fetch 404 → the old code silently returned.)
    // Recovery on a later reload comes from the persisted server branch row.
    const dropStranded = (content: any) => {
      // Dual-write stranded branch error to the store. Run RETAINED.
      execTable.release(placeholderBranch.id);
      deps.store.dispatch(runErrored(placeholderBranch.id, { code: "result_unavailable" }));
      recordUiEvent({
        event: "branch.reattach_terminal",
        placeholderId: handle.placeholderId,
        traceId: handle.traceId,
        outcome: "stranded_drop",
      });
      void content;
    };
    // NON-DESTRUCTIVE variant for a result that 404s even AFTER the server's bounded
    // retry. The backend stores results per-instance and ~30% of fetches transiently
    // 404; the server retry pushes that >99%, but on the rare residual miss we must NOT
    // delete a valid branch (it'd vanish from the lane one stochastic 404 at a time).
    // Keep the branch run in an error state marked recoverable so a reload re-attempts
    // the fetch (the result usually reappears on another instance).
    const keepStranded = (content: any) => {
      // Dual-write kept-stranded branch error to the store (retain run).
      execTable.release(placeholderBranch.id);
      deps.store.dispatch(
        runErrored(placeholderBranch.id, {
          code: "result_unavailable",
          message: "This scenario's result isn't available yet. Reload to try again.",
        }),
      );
      recordUiEvent({
        event: "branch.reattach_terminal",
        placeholderId: handle.placeholderId,
        traceId: handle.traceId,
        outcome: "stranded_keep",
      });
      void content;
    };
    if (!resultUrl) {
      dropStranded(`Couldn't reopen the scenario "${handle.modification}". Run it again.`);
      return;
    }

    // Fetch the result + replace placeholder with the real branch (typed api seam, P5.11).
    // getResult never throws and folds every failure into `ok:false` with a `status`: a 404
    // (stochastic per-instance miss, after the server's own retry) → keepStranded so the
    // branch survives a reload; any other non-ok (500/403, a network failure → status 0, or a
    // 2xx with a null/bad body → status carried through) → dropStranded. When ok, `rr.result`
    // is a real ForecastResult.
    const rr = await deps.services.api.getResult(handle.traceId);
    if (!rr.ok) {
      if (rr.status === 404)
        keepStranded(
          `The result for "${handle.modification}" isn't available yet. Reload to try again.`,
        );
      else
        dropStranded(
          `The result for "${handle.modification}" is no longer available. Run the scenario again.`,
        );
      return;
    }
    const result: any = rr.result; // decision C: verbatim code reads result loosely
    // Shared event filter/stamp — now consolidated onto filterAndStampEvents like
    // the other 3 sites. This path previously carried an inline copy MISSING the
    // isNaN(dEnd.getTime()) guard, so a branch-reattach event with a valid `date`
    // but a present-but-unparseable `date_end` was stamped isPast:false
    // unconditionally (Invalid Date <= anything is false) instead of falling back
    // to `date`. The helper has that guard, closing the divergence.
    const evtsList = Array.isArray(result.events) ? filterAndStampEvents(result.events) : [];
    const completedBranch = {
      ...placeholderBranch,
      _loading: false,
      events: evtsList,
      probability: result.contract?.probability ?? null,
      serverPrediction: boundedProbability(result.contract?.probability),
      contract: withBackground(result.contract) || null,
      serverExplanation: result.explanation || "",
      serverContext: {
        explanation: result.explanation || "",
        metadata: result.metadata || {},
        question: result.question || {},
        report: result.report || null,
      },
      report: result.report || null,
      seriesImpacts: {},
    };
    // Dual-write reattached branch completion to the store.
    execTable.release(placeholderBranch.id);
    deps.store.dispatch(
      runCompleted(placeholderBranch.id, {
        contract: completedBranch.contract || null,
        serverPrediction: completedBranch.serverPrediction,
        events: completedBranch.events || [],
        serverContext: completedBranch.serverContext || null,
        serverExplanation: completedBranch.serverExplanation,
        report: completedBranch.report,
        obs: resumedRunObs(
          selectRun(deps.store.getState(), placeholderBranch.id)?.obs,
          runningCatchup,
          (opts.now || Date.now)(),
        ),
      }),
    );
    // The completed branch is in the store, so the next save POST persists it and a
    // reload reconstructs it from the server branch row.
    recordUiEvent({
      event: "branch.reattach_terminal",
      placeholderId: handle.placeholderId,
      traceId: handle.traceId,
      outcome: "completed",
    });
  } finally {
    // This reattach attempt has settled (done / error / not-found / abort) —
    // release the in-flight lock so a genuine later resume (e.g. after reload)
    // can run.
    execTable.release(handle.placeholderId); // exec keyed by runId
  }
}

/**
 * The unified hydrate+resume sweep — the SOLE cross-reload resume path for running ROOT
 * runs and in-flight BRANCHES. `bootForecasts` calls this ONLY after `loadForecasts` has
 * resolved and the restore decision has applied, so the store is guaranteed hydrated by the
 * time this runs — no load-completion poll or fire-once guard needed (P6; those lived here
 * only because the old mount effect could race the load effect). Resume EVERY running root
 * that carries a traceId (focused vs. passive per the compare/URL focus rules) and every
 * running branch. Idempotent: each reattach claims `execTable` by runId, so a row already
 * streaming in this tab no-ops. `cancelledRef` is owned by the calling effect (its cleanup
 * flips `.current`) — still honored here in case boot itself gets cancelled mid-sweep.
 */
export async function resumeForecasts(
  deps: ResumeDeps,
  cancelledRef: any,
  { pruneMissingCompareHandles = true } = {},
) {
  if (cancelledRef.current) return;
  let plan;
  try {
    plan = planResume(deps.store.getState());
  } catch (e) {
    console.warn("Ring1 resume sweep: planResume failed", e);
    return;
  }
  // Pick the ONE focused root: the restored activeRunId if it's itself a running
  // root, else fall back to the MOST RECENT running root so SOME root owns
  // active/phase + the awaiting animation. selectRunsByStatus/planResume preserve
  // insertion order (not recency), so resolve runs and sort by createdAt desc here.
  // Without the fallback, a null activeRunId (store hydrated a running row with no
  // PENDING/ACTIVE handle) would send EVERY root to the passive lane, nothing focused.
  const mains = plan.filter((p) => p.kind === "main");
  // Every fresh durable comparePending handle participates in recovery, including handles
  // from an older generation that was still live when a newer pair started. Only ONE
  // generation may control comparison focus/UI, though: folding generations together would
  // turn two independent pairs into a 3-4-leg comparison on reload.
  // Keep handles for hydrated RUNNING legs regardless of age. The persisted absolute
  // recovery deadline limits active waiting; it is not evidence that the run died.
  // An authoritative hydration may prune terminal/missing legs below.
  const runningMainIds = new Set(mains.map((main) => main.runId));
  const comparePendingLegs = (() => {
    try {
      const retained = pruneComparePending(
        (leg: any) => leg && (!pruneMissingCompareHandles || runningMainIds.has(leg.clientId)),
      );
      // A failed hydration cannot prove a missing handle terminal, so retain it for a
      // later reload but do not select/resume it against an absent run in this sweep.
      return retained.filter((leg: any) => runningMainIds.has(leg.clientId));
    } catch (_e) {
      return [];
    }
  })();
  const storeRootIds = deps.store.getState().comparison?.rootIds ?? [];
  // One live-store snapshot reused by the URL id-resolution below (the compare
  // fallback + the single-root focus authority). The plan/focus computation from
  // here to the resume for-loop is synchronous, so a single read is consistent;
  // the for-loop still reads deps.store.getState() fresh per run as the store advances.
  const runsById = deps.store.getState().runsById;
  // Cross-tab fallback: a new tab has no comparePending handle, and comparison.rootIds
  // may not be URL-hydrated yet on the sweep's single run (compareHydratedRef runs later,
  // gated on forecastsLoaded). Parse the ?compare= ids straight from the URL (primary-
  // first order preserved) and resolve them against the hydrated runs by forecastId or
  // runId, so compare styling/focus is reliable cross-tab instead of racing hydration.
  const urlCompareRunIds = (() => {
    try {
      const wanted = readViewFromLocation().compareIds;
      if (wanted.length < 2) return [];
      const byId = Object.values(runsById);
      return wanted
        .slice(0, 2)
        .map((id) => byId.find((r) => r.forecastId === id || r.runId === id)?.runId)
        .filter(Boolean);
    } catch (_e) {
      return [];
    }
  })();
  // Explicit view state wins: the URL is boot's focus authority, followed by an already-
  // hydrated store comparison. With neither available, select the newest complete pending
  // generation. All other pending generations still resume below as passive roots and keep
  // their per-trace engine metadata; they simply do not join this UI pair.
  const pendingGroups = new Map<string, any[]>();
  for (const leg of comparePendingLegs) {
    if (!leg?.pairId || !leg?.clientId) continue;
    const group = pendingGroups.get(leg.pairId) ?? [];
    group.push(leg);
    pendingGroups.set(leg.pairId, group);
  }
  const newestPendingPair = [...pendingGroups.values()]
    .filter((group) => new Set(group.map((leg) => leg.clientId)).size >= 2)
    .reduce<any[] | undefined>((newest, group) => {
      if (!newest) return group;
      const generationBirth = (legs: any[]) =>
        Math.min(...legs.map((leg) => leg.generationStartedAt ?? leg.submittedAt ?? 0));
      const newestAt = generationBirth(newest);
      const groupAt = generationBirth(group);
      // On an equal birth timestamp the later-appended generation wins. Date.now()
      // can legitimately stamp two fast submissions in the same millisecond.
      return groupAt >= newestAt ? group : newest;
    }, undefined);
  const pendingPrimary = newestPendingPair?.find((leg) => leg.primary) ?? newestPendingPair?.[0];
  const orderedPendingPair = pendingPrimary
    ? [pendingPrimary, ...(newestPendingPair ?? []).filter((leg) => leg !== pendingPrimary)].slice(
        0,
        2,
      )
    : [];
  const selectedCompareRunIds =
    urlCompareRunIds.length >= 2
      ? urlCompareRunIds
      : storeRootIds.length >= 2
        ? storeRootIds.slice(0, 2)
        : orderedPendingPair.map((leg) => leg.clientId);
  const selectedPendingFallback =
    urlCompareRunIds.length < 2 && storeRootIds.length < 2 && selectedCompareRunIds.length >= 2;
  if (selectedPendingFallback) {
    deps.store.dispatch(comparisonSet(selectedCompareRunIds));
    if (deps.ui.debugUI) deps.ui.setCompareMode?.(true);
  }
  const compareLegRunIds = new Set(selectedCompareRunIds);
  const isComparePair = compareLegRunIds.size >= 2;
  // Each source is primary-first. Because the selected ids come from exactly one source,
  // the primary can never leak in from an older retained generation.
  const primaryCandidate = selectedCompareRunIds[0] ?? null;
  const orderedCompareLegRunIds = [
    primaryCandidate,
    ...[...compareLegRunIds].filter((id) => id && id !== primaryCandidate),
  ];
  // URL is the boot focus authority (EPIC #157 Lane E2 / #168): a forecast the
  // user was looking at while it streamed carries `?forecast=<id>` in the URL
  // (the installUrlSync mirror wrote it, upgrading the client id to the stable
  // forecastId once allocated). On reload we FOCUS + resume that still-running
  // root FOREGROUND ({} opts below — owns active/phase/chat, seeds "Resuming
  // forecast…") rather than leaving it passive. resolveSingleRootFocus returns
  // null unless the URL names a still-running root (a MAIN in `plan`), so
  // blank-home boot — a single running root NOT auto-focused, resuming passively
  // in the secondary lane, nothing focused — is preserved for every case the URL
  // doesn't explicitly claim: a bare/cross-tab load with no `?forecast=`, a URL
  // naming a TERMINAL forecast (that focus is restored by the forecastDeepLink
  // boot effect, not here), or a "gone" id. Compare PAIRS still focus a primary
  // leg via focusedRunId below (unchanged); the URL active id is the single-root
  // fallback only.
  const urlActiveId = (() => {
    try {
      return readViewFromLocation().activeId;
    } catch (_e) {
      return null;
    }
  })();
  const singleRootFocus = resolveSingleRootFocus(
    urlActiveId,
    runsById,
    mains.map((m) => m.runId),
  );
  // Compare pair → the first RUNNING compare leg (primary-first ordering). If NONE of
  // the compare legs is actually running — e.g. a stale `?compare=a,b` URL from an older,
  // completed compare view makes isComparePair true while neither leg is in `mains` —
  // fall back to the single-root focus so a concurrent running single forecast still gets
  // focused (rather than stranding every running root passively behind a non-running id).
  const focusedRunId = isComparePair
    ? (orderedCompareLegRunIds.find((id) => mains.some((m) => m.runId === id)) ?? singleRootFocus)
    : singleRootFocus;
  // Recover the per-leg/single-forecast engine from the kept sessionStorage
  // handles: a still-running row's server_ctx is '{}' until completion, so run.backend
  // hydrates null. The handles (written at submit, still present on a same-tab reload)
  // carry the real engine, keyed by traceId — the same durable source the deleted
  // reattach effects used. Store-first (run.backend wins at the use site); among handles
  // first-writer wins (??). Includes the single-slot PENDING_FORECAST_KEY so a focused
  // single forecast keeps its engine/lane cue too. Cross-tab (empty sessionStorage) →
  // stays null, same as the rest of the store-native sweep. (Branches take their
  // engine from the hydrated run's backend in the kind:'branch' path below.)
  const engineByTrace: Record<string, any> = {};
  const recoveryDeadlineByTrace: Record<string, number> = {};
  try {
    const sf = sessionStorage.getItem(PENDING_FORECAST_KEY);
    if (sf) {
      const h = JSON.parse(sf);
      if (h?.traceId && h.engine) engineByTrace[h.traceId] = h.engine;
      if (h?.traceId && Number.isFinite(h.recoveryDeadlineAt))
        recoveryDeadlineByTrace[h.traceId] = h.recoveryDeadlineAt;
    }
  } catch (_e) {
    /* best-effort */
  }
  for (const leg of comparePendingLegs)
    if (leg?.traceId) {
      if (leg.engine) engineByTrace[leg.traceId] = engineByTrace[leg.traceId] ?? leg.engine;
      if (Number.isFinite(leg.recoveryDeadlineAt))
        recoveryDeadlineByTrace[leg.traceId] =
          recoveryDeadlineByTrace[leg.traceId] ?? leg.recoveryDeadlineAt;
    }
  // Eagerly drop an irrelevant PENDING_FORECAST_KEY handle. The terminal-clear
  // below only fires when the single forecast is actually resumed here (its run is a
  // running main in `plan`). If that run finished server-side while the tab was away
  // (so it's no longer RUNNING and isn't in `plan`) or is missing, the
  // handle would otherwise linger and PERMANENTLY divert the unified restore
  // dispatcher: a present PENDING_FORECAST_KEY makes decideRestore return a
  // `reattach` (pendingId) decision, so the dispatcher defers last-viewed restore
  // to this sweep instead of selecting it. Remove it now in those cases (when it
  // IS being resumed, leave it for the terminal clear so the dispatcher keeps
  // deferring to this sweep until active is owned).
  try {
    const sfRaw = sessionStorage.getItem(PENDING_FORECAST_KEY);
    if (sfRaw) {
      const sfh = JSON.parse(sfRaw);
      const willResume =
        sfh?.clientId && plan.some((p) => p.kind === "main" && p.runId === sfh.clientId);
      if (!willResume) sessionStorage.removeItem(PENDING_FORECAST_KEY);
    }
  } catch (_e) {
    /* ignore */
  }
  for (const { runId, kind } of plan) {
    if (execTable.has(runId)) continue; // already streaming in this tab
    if (kind === "branch") {
      // C1b: resume in-flight BRANCHES from the store — the SOLE branch-reattach
      // path. The branch run is already hydrated by the initial /api/forecasts
      // load (the branch row is persisted at creation with status='running' +
      // traceId), and reattachBranch claims execByRunIdRef by placeholderId
      // (= the branch runId) so this is idempotent against any live stream.
      // Branches always resume PASSIVELY (they never own active/phase), so
      // they're not part of the focused-leg logic above. Reconstruct the
      // reattachBranch handle from the store run `r` (and its root/parent runs).
      const r = deps.store.getState().runsById[runId];
      if (!r) continue; // hydrated run gone (deleted in another tab)
      const handle = branchReattachHandle(deps.store.getState(), r);
      reattachBranch(deps, handle).catch((e) => {
        console.warn("Ring1 resume sweep: branch resume failed", runId, e);
      });
      continue;
    }
    const run = deps.store.getState().runsById[runId];
    if (!run) continue;
    const isCompareLeg = isComparePair && compareLegRunIds.has(runId);
    const hasComparePendingHandle = comparePendingLegs.some(
      (leg) => leg.traceId && leg.traceId === run.traceId,
    );
    // A resumed compare leg already passed preprocess before it started (onTraceForRoot
    // stamped preprocessConfirmed:true), so stamp it on the pending too — the reattach's
    // re-pop guard is `seedChat && pending.preprocessed && !pending.preprocessConfirmed`;
    // this keeps the panel from re-popping if a preprocessed payload is ever carried.
    const pending = {
      clientId: run.runId,
      traceId: run.traceId,
      forecastId: run.forecastId || null,
      question: run.question || "",
      engine: run.backend || engineByTrace[run.traceId as any] || null,
      contract: run.contract || null,
      submittedAt: run.createdAt,
      // The recovery window counts from this run's start (restamped on a reforecast), not the
      // forecast's creation, which would expire an older forecast's at once.
      recoveryStartedAt: run.startedAt ?? undefined,
      recoveryDeadlineAt: recoveryDeadlineByTrace[run.traceId as any],
      ...(isCompareLeg ? { preprocessConfirmed: true } : {}),
    };
    // Opts:
    //  - compare leg, primary (focused) → {compare:true}: owns active/phase/chat and
    //    the reconnected placeholder is engine-prefixed (matches the live compare run).
    //  - compare leg, secondary → {lane:"secondary",seedChat:false,compare:true}: drives
    //    its stream + row only, but still writes its OWN engine-prefixed "Resuming…" line.
    //  - non-compare focused root → {} (resume normally, owns active/phase/chat).
    //  - non-compare background root → {lane:"secondary",seedChat:false}: drives only its
    //    stream + store row, NO chat (no compare:true → isCompareLeg false in reattach).
    let opts;
    if (isCompareLeg)
      opts =
        runId === focusedRunId
          ? { compare: true }
          : { lane: "secondary", seedChat: false, compare: true };
    else {
      const clearPendingOpts = hasComparePendingHandle
        ? { clearComparePendingOnTerminal: true }
        : {};
      opts =
        runId === focusedRunId
          ? clearPendingOpts
          : {
              lane: "secondary",
              seedChat: false,
              ...clearPendingOpts,
            };
    }
    // reattachToForecast already dispatches runErrored to the store on failure
    // (the run shows as errored); log here too so a background resume failure is
    // visible in devtools rather than vanishing into a bare promise rejection.
    const resumeP = reattachToForecast(deps, pending, cancelledRef, opts).catch((e) => {
      console.warn("Ring1 resume sweep: resume failed", runId, e);
    });
    // Clear the single-slot PENDING_FORECAST_KEY once the single-forecast
    // resume reaches a terminal — mirrors the deleted PENDING_FORECAST_KEY mount
    // effect's finally. The key's only remaining consumer is the unified restore
    // dispatcher: while it's set, decideRestore returns a `reattach` (pendingId)
    // decision, so leaving it set would divert last-viewed-forecast restore to this
    // sweep on every later reload in this tab. Cleared on TERMINAL (not eagerly) so
    // the resume already owns `active` by then. Compare resumes use their own
    // comparePending handles, cleared on their own terminals; PENDING_FORECAST_KEY
    // is the single-forecast slot only.
    if (!isCompareLeg)
      resumeP.then((terminal) => {
        if (terminal) clearPendingForecastIfOwned(run.traceId, run.runId);
      });
  }
}

/**
 * The `reattachBranch` handle for a hydrated branch run `r`, reconstructed from the store. Its
 * root and parent are read from either slice: a branch of a forecast shared with the viewer has
 * them in `sharedRunsById`. `rootId` defaults to the stored `conversationId`; a shared tree
 * hydrated across pages passes the root resolved through `parentRunId` (`treeRootOf`).
 */
function branchReattachHandle(state: any, r: any, rootId: string = r.conversationId) {
  const root = selectRun(state, rootId);
  const parent = r.parentRunId ? selectRun(state, r.parentRunId) : null;
  return {
    placeholderId: r.runId,
    traceId: r.traceId,
    branchForecastId: r.forecastId,
    rootClientId: rootId,
    rootForecastId: root?.forecastId ?? null,
    parentClientId: r.parentRunId,
    parentForecastId: parent?.forecastId ?? null,
    parentBranchId: r.parentRunId === rootId ? null : r.parentRunId,
    modification: r.modification,
    forkDate: r.forkDate,
    forkEvent: r.forkEvent,
    // Branches take their engine from the hydrated run: the engine-by-trace map the sweep
    // builds holds root handles only.
    engine: r.backend || null,
    submittedAt: r.createdAt,
  };
}

/** What `resumeSharedRerun` did. "waiting": its run has not hydrated into the shared lane yet. */
export type SharedRerunResume = "waiting" | "dropped" | "resumed";

/** The store and the bound `reattachToForecast` / `reattachBranch` (deps applied). */
export interface SharedRerunEnv {
  store: CommandDeps["store"];
  reattach: (pending: any, cancelledRef: any, opts?: any) => Promise<boolean | undefined>;
  reattachBranch: (handle: any) => Promise<unknown>;
}

/**
 * Reattach one update this tab started on a forecast SHARED with it (an in-org collaborator's
 * Update now), named by its `SHARED_RERUN_KEY` entry `handle`, once the shared lane holds its
 * run: the root rerun and the branch reruns under it.
 * The boot sweep (`resumeForecasts`) covers only the viewer's own forecasts and runs before the
 * shared list loads. Only the root the entry names (on the trace it names) is reattached, with
 * the branch runs under it, so a tab does not attach to a root update another tab or person
 * started; the runs stay in the shared slice. The root is focused (owns
 * active/phase/chat) only when the URL names it, as the sweep does for a root of the viewer's
 * own; otherwise passive. Branches always resume passively.
 *
 * The handle stays while anything it names is still running, so a second reload reattaches
 * whatever is left (`settleSharedRerunHandle`); it is dropped at once when its root is on
 * another trace or no longer editable. Those decisions read the whole tree, so the caller loads
 * it first (`loadSharedRerunRuns`): a branch can sit on a later page than its root.
 *
 */
export function resumeSharedRerun(
  env: SharedRerunEnv,
  cancelledRef: any,
  handle: { runId: string; traceId: string },
): SharedRerunResume {
  const state: any = env.store.getState();
  const run: any = state.sharedRunsById?.[handle.runId];
  if (!run) return "waiting";
  if (run.traceId !== handle.traceId || !canUpdateNow(run)) {
    clearSharedRerunHandle(handle.runId, handle.traceId);
    return "dropped";
  }
  // Both slices: a branch the viewer created on the shared forecast is in their own, and the
  // boot sweep cannot reattach it while its root has not hydrated.
  const settle = () =>
    settleSharedRerunHandle(
      selectPremiseRunsById(env.store.getState()),
      handle.runId,
      handle.traceId,
    );
  const isRunning = (r: any) => r.status === STATUS.RUNNING && Boolean(r.traceId);
  const all = selectPremiseRunsById(state);
  const branches = Object.values(all).filter(
    (r: any) => r.parentRunId != null && treeRootOf(all, r.runId) === run.runId && isRunning(r),
  );
  if (!isRunning(run) && !branches.length) {
    // Nothing left that a reload could reattach.
    clearSharedRerunHandle(handle.runId, handle.traceId);
    return "dropped";
  }
  for (const br of branches) {
    if (execTable.has((br as any).runId)) continue; // already streaming in this tab
    env
      .reattachBranch(branchReattachHandle(state, br, run.runId))
      .catch((e) => {
        console.warn("shared rerun resume: branch resume failed", (br as any).runId, e);
      })
      .finally(settle);
  }
  if (!isRunning(run) || execTable.has(run.runId)) return "resumed";
  let focused = false;
  try {
    const urlId = readViewFromLocation().activeId;
    focused = !!urlId && (urlId === run.runId || urlId === run.forecastId);
  } catch (_e) {
    focused = false;
  }
  const pending = {
    clientId: run.runId,
    traceId: run.traceId,
    forecastId: run.forecastId || null,
    question: run.question || "",
    engine: run.backend || null,
    contract: run.contract || null,
    submittedAt: run.createdAt,
    // The recovery window counts from THIS run's start, which the server restamps on a
    // reforecast; createdAt is the forecast's, and would expire an older forecast's at once.
    recoveryStartedAt: run.startedAt ?? undefined,
  };
  env
    .reattach(pending, cancelledRef, focused ? {} : { lane: "secondary", seedChat: false })
    .catch((e) => {
      console.warn("shared rerun resume failed", run.runId, e);
    })
    .finally(settle);
  return "resumed";
}

/** `resumeSharedRerun` for every update this tab recorded, keyed by run id. */
export function resumeSharedReruns(
  env: SharedRerunEnv,
  cancelledRef: any,
): Record<string, SharedRerunResume> {
  const out: Record<string, SharedRerunResume> = {};
  for (const handle of readSharedRerunHandles())
    out[handle.runId] = resumeSharedRerun(env, cancelledRef, handle);
  return out;
}

/**
 * Clear the shared-rerun handle once nothing it names is still running: its root `runId` and the
 * branch runs under it in `runsById` (pass both slices: `selectPremiseRunsById`), found through
 * `parentRunId` (`treeRootOf`). A branch rerun can outlast its root, and a reload then has to
 * reattach it.
 */
export function settleSharedRerunHandle(
  runsById: Record<string, any> | null | undefined,
  runId: string,
  traceId: string | null = null,
): void {
  const runs = runsById ?? {};
  const busy = Object.values(runs).some(
    (r: any) => r.status === STATUS.RUNNING && treeRootOf(runs, r.runId) === runId,
  );
  if (!busy) clearSharedRerunHandle(runId, traceId);
}

/** At most this many shared-list pages are read looking for a shared rerun's forecast. */
export const SHARED_RERUN_SEARCH_MAX_PAGES = 40;

/**
 * Load the whole tree of every update the `SHARED_RERUN_KEY` entries name into the shared lane
 * before `resumeSharedReruns` decides anything: the shared list pages by grant time, so a root
 * can be past the first page the lane loads by itself, and its branches can sit on later pages
 * than it, a sub-branch even before its parent. Every row of a tree carries its root's grant
 * time (`receivedAt`), and rows come newest grant first, so once every root is loaded paging
 * stops at the first page that reaches an older grant than the oldest root's; without a grant
 * time it reads to the end. An entry whose root the list ends without (access revoked, or the
 * forecast deleted) is cleared.
 *
 * @returns "found" (every recorded tree that still exists is loaded), "absent" (none does; their
 *   entries cleared), "failed" (a page failed, or the page cap was reached first: trees may be
 *   partial and the entries stay), or "none" (nothing recorded)
 */
export async function loadSharedRerunRuns(
  deps: CommandDeps,
  {
    cancelled,
    onPinned,
    maxPages = SHARED_RERUN_SEARCH_MAX_PAGES,
  }: {
    cancelled?: () => boolean;
    onPinned?: (pinned: Record<string, unknown>) => void;
    maxPages?: number;
  } = {},
): Promise<"found" | "absent" | "failed" | "none"> {
  const handles = readSharedRerunHandles();
  if (!handles.length) return "none";
  const grantMs = (r: any) => Date.parse(r?.receivedAt ?? "");
  const rootGrants = new Map<string, number>();
  let cursor: string | null = null;
  for (let page = 0; page < maxPages; page++) {
    const res = await loadSharedForecasts(deps, { cursor, cancelled });
    if (!res.ok) return "failed";
    onPinned?.(res.pinned);
    const runs: any = deps.store.getState().sharedRunsById ?? {};
    for (const h of handles)
      if (!rootGrants.has(h.runId) && runs[h.runId])
        rootGrants.set(h.runId, grantMs(runs[h.runId]));
    const grants = [...rootGrants.values()];
    if (rootGrants.size === handles.length && grants.every(Number.isFinite)) {
      const oldest = Math.min(...res.runIds.map((id) => grantMs(runs[id])).filter(Number.isFinite));
      if (oldest < Math.min(...grants)) return "found"; // past every tree's last row
    }
    if (!res.nextPageToken) {
      for (const h of handles)
        if (!rootGrants.has(h.runId)) clearSharedRerunHandle(h.runId, h.traceId);
      return rootGrants.size ? "found" : "absent";
    }
    cursor = res.nextPageToken;
  }
  return "failed";
}
